import { filterJobLocations } from '../../../utils/data/filter-job-locations';
import type { DisplayJob } from '../../../types';

/**
 * Location display data structure
 */
export interface LocationDisplayData {
  display: string;
  fullList: string;
  allLocations: string[];
  allLocationsForUrl: string[];
}

/**
 * Single prioritized location data
 */
export interface PrioritizedLocationData {
  display: string;
  locationForUrl: string;
  additionalCount: number;
  fullList: string;
}

/**
 * Gets the first city/location as a string for URLs
 * Returns the full location value (with country) for proper URL generation
 */
export function getFirstLocationString(hit: DisplayJob): string {
  if (Array.isArray(hit.location) && hit.location.length > 0) {
    return String(hit.location[0] || '');
  } else {
    const loc: string = String(hit.location || '');
    if (loc.trim() !== '') {
      // Return the full location string (with country) for URL generation
      // The URL generation will convert "City, Country" to "City_Country"
      return loc.trim();
    }
  }
  return '';
}

/**
 * Gets location display with count (e.g., "Helsinki +5")
 */
export function getLocationDisplayWithCount(hit: DisplayJob): LocationDisplayData {
  if (Array.isArray(hit.location) && hit.location.length > 0) {
    // Filter locations based on business rules (show only Finland + remote regional if Finland exists)
    const filteredLocations = filterJobLocations(hit.location);
    
    if (filteredLocations.length === 0) {
      return { display: '', fullList: '', allLocations: [], allLocationsForUrl: [] };
    }
    
    const firstLocation = String(filteredLocations[0] || '');
    const cleanLoc = firstLocation.replace(/,\s*Finland$/, '').trim();
    const additionalCount = filteredLocations.length - 1;
    const display = additionalCount > 0 ? `${cleanLoc} +${additionalCount}` : cleanLoc;
    const fullList = filteredLocations.join(', ');
    // Clean all locations for display
    const allLocations = filteredLocations.map(loc => String(loc || '').replace(/,\s*Finland$/, '').trim()).filter(loc => loc);
    // Keep original locations for URL generation (with country)
    const allLocationsForUrl = filteredLocations.map(loc => String(loc || '').trim()).filter(loc => loc);
    return { display, fullList, allLocations, allLocationsForUrl };
  } else {
    const loc: string = String(hit.location || '');
    if (loc.trim() !== '') {
      // Try to parse multiple locations from string
      const firstLocation = loc.includes(',') ? loc.split(',').slice(0, 2).join(',').trim() : loc.trim();
      const cleanLoc = firstLocation.replace(/,\s*Finland$/, '').trim();
      
      // Estimate location count (rough estimate based on country names)
      const locationPairs = loc.split(/,\s*[A-Z][a-z]+(?=,|$)/);
      const totalLocations = Math.max(1, Math.floor(locationPairs.length));
      const additionalCount = totalLocations > 1 ? totalLocations - 1 : 0;
      
      const display = additionalCount > 0 ? `${cleanLoc} +${additionalCount}` : cleanLoc;
      
      // Parse all locations from string (format: "City1, Country1, City2, Country2, ...")
      const allLocations: string[] = [];
      const allLocationsForUrl: string[] = [];
      if (loc.includes(',')) {
        // Split by commas and group into city-country pairs
        const parts = loc.split(',').map(p => p.trim()).filter(p => p);
        // Group into pairs (city, country)
        for (let i = 0; i < parts.length; i += 2) {
          if (i + 1 < parts.length) {
            // We have a city and country pair
            const city = parts[i];
            const country = parts[i + 1];
            const locationPair = `${city}, ${country}`;
            const cleanLocationPair = locationPair.replace(/,\s*Finland$/, '').trim();
            if (cleanLocationPair) allLocations.push(cleanLocationPair);
            if (locationPair) allLocationsForUrl.push(locationPair);
          } else if (parts[i]) {
            // Single location without country
            const cleanSingleLoc = parts[i].replace(/,\s*Finland$/, '').trim();
            if (cleanSingleLoc) allLocations.push(cleanSingleLoc);
            allLocationsForUrl.push(parts[i]);
          }
        }
        // If parsing failed or only got one location, fall back to first location
        if (allLocations.length === 0) {
          allLocations.push(cleanLoc);
          allLocationsForUrl.push(firstLocation);
        }
      } else {
        allLocations.push(cleanLoc);
        allLocationsForUrl.push(loc);
      }
      
      // Filter locations based on business rules (show only Finland + remote regional if Finland exists)
      const filteredLocationsForUrl = filterJobLocations(allLocationsForUrl);
      
      if (filteredLocationsForUrl.length === 0) {
        return { display: '', fullList: '', allLocations: [], allLocationsForUrl: [] };
      }
      
      // Rebuild allLocations based on filtered locations
      const filteredAllLocations = filteredLocationsForUrl.map(loc => String(loc || '').replace(/,\s*Finland$/, '').trim()).filter(loc => loc);
      
      // Update display and fullList based on filtered locations
      const filteredFirstLocation = String(filteredLocationsForUrl[0] || '');
      const filteredCleanLoc = filteredFirstLocation.replace(/,\s*Finland$/, '').trim();
      const filteredAdditionalCount = filteredLocationsForUrl.length - 1;
      const filteredDisplay = filteredAdditionalCount > 0 ? `${filteredCleanLoc} +${filteredAdditionalCount}` : filteredCleanLoc;
      const filteredFullList = filteredLocationsForUrl.join(', ');
      
      return { display: filteredDisplay, fullList: filteredFullList, allLocations: filteredAllLocations, allLocationsForUrl: filteredLocationsForUrl };
    }
  }
  return { display: '', fullList: '', allLocations: [], allLocationsForUrl: [] };
}

/**
 * Gets a single prioritized location for compact cards
 * Prioritizes: 1) User's location filter, 2) Finland locations, 3) First location
 */
export function getSinglePrioritizedLocation(
  hit: DisplayJob,
  userLocationFilter: string | string[] | null | undefined
): PrioritizedLocationData {
  // Get all filtered locations
  let allLocations: string[] = [];
  let allLocationsForUrl: string[] = [];
  
  if (Array.isArray(hit.location) && hit.location.length > 0) {
    const filteredLocations = filterJobLocations(hit.location);
    allLocationsForUrl = filteredLocations;
    allLocations = filteredLocations.map(loc => String(loc || '').replace(/,\s*Finland$/, '').trim()).filter(loc => loc);
  } else if (hit.location) {
    const loc: string = String(hit.location || '');
    if (loc.trim() !== '') {
      // Parse locations from string format
      const parts = loc.split(',').map(p => p.trim()).filter(p => p);
      for (let i = 0; i < parts.length; i += 2) {
        if (i + 1 < parts.length) {
          const locationPair = `${parts[i]}, ${parts[i + 1]}`;
          allLocationsForUrl.push(locationPair);
          allLocations.push(locationPair.replace(/,\s*Finland$/, '').trim());
        } else if (parts[i]) {
          allLocationsForUrl.push(parts[i]);
          allLocations.push(parts[i].replace(/,\s*Finland$/, '').trim());
        }
      }
      // Filter based on business rules
      const filteredLocationsForUrl = filterJobLocations(allLocationsForUrl);
      allLocationsForUrl = filteredLocationsForUrl;
      allLocations = filteredLocationsForUrl.map(loc => String(loc || '').replace(/,\s*Finland$/, '').trim()).filter(loc => loc);
    }
  }
  
  if (allLocations.length === 0) {
    return { display: '', locationForUrl: '', additionalCount: 0, fullList: '' };
  }
  
  // Normalize user location filter for comparison
  let normalizedUserFilter: string | null = null;
  let normalizedUserFilterCity: string | null = null;
  if (userLocationFilter) {
    if (Array.isArray(userLocationFilter)) {
      normalizedUserFilter = userLocationFilter[0] || null;
    } else {
      normalizedUserFilter = userLocationFilter;
    }
    if (normalizedUserFilter) {
      // Normalize: remove country suffix and get city name
      normalizedUserFilter = normalizedUserFilter.replace(/,\s*Finland$/, '').trim().toLowerCase();
      // Extract just the city name (before any comma)
      normalizedUserFilterCity = normalizedUserFilter.split(',')[0].trim().toLowerCase();
    }
  }
  
  // Find prioritized location
  let prioritizedIndex = 0;
  let prioritizedLocationForUrl = allLocationsForUrl[0];
  let prioritizedLocation = allLocations[0];
  
  // 1. Check if user has a location filter - prioritize matching location
  if (normalizedUserFilterCity) {
    for (let i = 0; i < allLocations.length; i++) {
      const cleanLoc = allLocations[i].toLowerCase();
      const locForUrl = allLocationsForUrl[i];
      const locCity = cleanLoc.split(',')[0].trim().toLowerCase();
      // Check if city name matches user filter
      if (locCity === normalizedUserFilterCity || cleanLoc.includes(normalizedUserFilterCity) || normalizedUserFilterCity.includes(locCity)) {
        prioritizedIndex = i;
        prioritizedLocation = allLocations[i];
        prioritizedLocationForUrl = locForUrl;
        break;
      }
    }
  } else {
    // 2. Prefer Finland locations if no user filter
    for (let i = 0; i < allLocationsForUrl.length; i++) {
      const locForUrl = allLocationsForUrl[i];
      if (locForUrl.toLowerCase().includes('finland') || locForUrl.toLowerCase().includes('suomi')) {
        prioritizedIndex = i;
        prioritizedLocation = allLocations[i];
        prioritizedLocationForUrl = locForUrl;
        break;
      }
    }
  }
  
  const additionalCount = allLocations.length - 1;
  const display = additionalCount > 0 ? `${prioritizedLocation} +${additionalCount}` : prioritizedLocation;
  const fullList = allLocationsForUrl.join(', ');
  
  return { display, locationForUrl: prioritizedLocationForUrl, additionalCount, fullList };
}
